import { Manifest } from "@iiif/presentation-3";

/**
 * IIIF Manifest Parser
 *
 * Parses IIIF (International Image Interoperability Framework) manifests
 * from DOM elements and provides convenient access to manifest data.
 *
 * The manifest is expected to be embedded in a script tag with id "iiif_manifest"
 * containing JSON data following IIIF Presentation API 3.0 specification.
 */
export class ManifestParser {
    /**
     * Initialize the manifest parser.
     * Automatically parses the manifest from the DOM on instantiation.
     */
    constructor(manifest) {
        this.manifest = manifest ? manifest : this.parsedManifest;
    }

    /**
     * Find the DOM element containing the IIIF manifest data.
     * Looks for a script tag with id "iiif_manifest".
     * @returns {HTMLScriptElement | null} The script element or null if not found
     */
    get manifestElement() {
        return document.querySelector("#iiif_manifest");
    }

    /**
     * Extract the raw JSON content from the manifest element.
     * Returns empty array as fallback if element is missing or empty.
     * @returns {string} Raw JSON string from the manifest element
     */
    get manifestContent() {
        return this.manifestElement?.textContent ?? "[]";
    }

    /**
     * Parse the raw manifest content into a JavaScript object.
     * Handles JSON parsing errors gracefully by logging and returning undefined.
     * @returns {Manifest | undefined} Parsed IIIF manifest object or undefined on error
     */
    get parsedManifest() {
        try {
            return JSON.parse(this.manifestContent);
        } catch (err) {
            console.error("Failed to parse IIIF manifest:", err);
            return undefined;
        }
    }

    /**
     * Get the thumbnail image URL optimized for maximum quality.
     *
     * Extracts the first thumbnail from the manifest and modifies the IIIF URL
     * to request maximum size instead of a specific dimension, ensuring sharp images.
     *
     * If this image is not available try several other sources from the manifest.
     *
     * URL transformation example:
     * "/full/400/" → "/full/max/"
     *
     * @returns {string | null} Optimized thumbnail URL or null if unavailable
     */
    get thumbnail() {
        if (!this.manifest) return null;
        let thumbnail =
            this.manifest.thumbnail?.[0]?.id ??
            this.manifest.thumbnail?.["@id"] ??
            this.fallbackThumbnail;
        if (!thumbnail) return null;
        return thumbnail.replace(/(\/full\/)([^/]+)(\/)/, "$1max$3");
    }
    /**
     * Get a fallback thumbnail image URL for when the manifest does not contain a thumbnail.
     *
     * @returns {string | null} Thumbnail URL or null if unavailable
     */
    get fallbackThumbnail() {
        if (!this.manifest) return null;

        /**
         * Recursively search through items for the first Image body id
         *
         * @param {Array} items
         * @param {number} depth
         * @returns {string | null}
         */
        const findImageId = (items, depth = 0) => {
            if (!items || depth > 5) return null;

            for (const item of items) {
                const body = item.body;

                if (Array.isArray(body)) {
                    const img = body.find((b) => b?.type === "Image" && b.id);
                    if (img) return img.id;
                } else if (body?.type === "Image" && body.id) {
                    return body.id;
                }

                // search deeper
                const deeper = findImageId(item.items, depth + 1);
                if (deeper) return deeper;
            }

            return null;
        };

        for (const canvas of this.manifest.items ?? []) {
            // --- Try 1: from regular items ---
            const fromItems = findImageId(canvas.items);
            if (fromItems) return fromItems;

            // --- Try 2: from placeholderCanvas ---
            if (canvas.placeholderCanvas) {
                const fromPlaceholder = findImageId(
                    canvas.placeholderCanvas.items
                );
                if (fromPlaceholder) return fromPlaceholder;
            }
        }

        return null;
    }

    /**
     * Get the title of the manifest in the preferred language.
     *
     * Follows IIIF internationalization patterns by checking:
     * 1. Dutch language label (nl)
     * 2. Fallback to language-neutral label (none)
     * 3. Returns the first available title string
     *
     * @returns {string | undefined} The manifest title or undefined if not available
     */
    get title() {
        return (this.manifest?.label?.nl ?? this.manifest?.label?.none)?.[0];
    }
}
